import React, { useState, useEffect, useRef } from 'react';
import { FaComments, FaTimes, FaPaperPlane, FaThumbsUp, FaThumbsDown, FaTrash } from 'react-icons/fa';
import axiosInstance from '../apis/axiosConfig';
import { useDarkMode } from '../hooks/useDarkMode';

// Default suggestions to show when backend returns empty
const DEFAULT_SUGGESTIONS = [
    "How do I analyze my website SEO?",
    "Check my subscription status",
    "I'm getting an error message",
];

// Format time in Asia/Kolkata timezone (same as backend)
const formatTimeInKolkata = (timestamp) => {
    if (!timestamp) return '';

    const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(timestamp);
    // If timestamp is naive (no tz), assume UTC from backend and convert to IST
    const normalizedTimestamp = hasTimezone ? timestamp : `${timestamp}Z`;
    const date = new Date(normalizedTimestamp);

    return date.toLocaleString('en-US', {
        timeZone: 'Asia/Kolkata',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
};


const Chatbot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [suggestions, setSuggestions] = useState(DEFAULT_SUGGESTIONS); // Show default suggestions on load
    const [isClearing, setIsClearing] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const chatContainerRef = useRef(null);
    const { isDarkMode } = useDarkMode();

    // Initialize session ID and load history
    useEffect(() => {
        const initializeChat = async () => {
            const storedSessionId = localStorage.getItem('chatbot_session_id');
            if (storedSessionId) {
                setSessionId(storedSessionId);
                await loadChatHistory(storedSessionId);
            } else {
                const newSessionId = `web_${Date.now()}_${Math.random().toString(36).substring(7)}`;
                setSessionId(newSessionId);
                localStorage.setItem('chatbot_session_id', newSessionId);
            }
        };
        initializeChat();
    }, []);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        if (messages.length > 0) {
            scrollToBottom();
        }
    }, [messages]);

    // Focus input when chat opens
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
            // Scroll to bottom when chat opens
            if (messages.length > 0) {
                scrollToBottom();
            }
        }
    }, [isOpen]);

    // Handle click outside to close chat
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (chatContainerRef.current && !chatContainerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 0);
    };

    // Load chat history from backend
    const loadChatHistory = async (sessionId) => {
        if (!sessionId) return;
        
        setIsLoadingHistory(true);
        try {
            const response = await axiosInstance.get(`/chatbot/history/${sessionId}/`);
            
            if (response.data.chat_history && response.data.chat_history.length > 0) {
                const formattedMessages = response.data.chat_history.map((msg) => ({
                    id: msg.message_id || Date.now() + Math.random(),
                    text: msg.content,
                    sender: msg.role === 'user' ? 'user' : 'bot',
                    timestamp: msg.timestamp,
                    messageId: msg.message_id,
                    feedback: msg.feedback || null,
                }));
                setMessages(formattedMessages);
            }
        } catch (error) {
            // For new sessions or non-existent history, it's normal to get an error
            // Start with empty chat - this is expected behavior for fresh accounts
            if (error.response?.status !== 500) {
                console.warn('Chat history not found (expected for new sessions)');
            }
            // Don't show error to user, just start with empty chat
        } finally {
            setIsLoadingHistory(false);
        }
    };

    // Handle feedback on messages
    const handleFeedback = async (messageId, feedbackType) => {
        if (!sessionId || !messageId) return;

        try {
            await axiosInstance.post('/chatbot/feedback/', {
                session_id: sessionId,
                message_id: messageId,
                feedback: feedbackType,
            });

            // Update local message state
            setMessages(prev => prev.map(msg => 
                msg.messageId === messageId 
                    ? { ...msg, feedback: feedbackType }
                    : msg
            ));
        } catch (error) {
            console.error('Error saving feedback:', error);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();

        if (!inputMessage.trim() || isLoading || !sessionId) return;

        const userMessage = {
            id: Date.now(),
            text: inputMessage,
            sender: 'user',
            timestamp: new Date().toISOString(),
        };

        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setIsLoading(true);

        try {
            const response = await axiosInstance.post('/chatbot/chat/', {
                message: inputMessage,
                session_id: sessionId,
            });

            const botMessage = {
                id: Date.now() + 1,
                text: response.data.response || response.data.message || 'I received your message.',
                sender: 'bot',
                timestamp: response.data.timestamp || new Date().toISOString(), // Use backend timestamp
                messageId: response.data.message_id, // Store message ID for feedback
                feedback: null,
            };

            setMessages(prev => [...prev, botMessage]);
            
            // Set suggestions from response
            if (response.data.suggestions && Array.isArray(response.data.suggestions) && response.data.suggestions.length > 0) {
                setSuggestions(response.data.suggestions);
            } else {
                setSuggestions(DEFAULT_SUGGESTIONS);
            }
        } catch (error) {
            console.error('Error sending message:', error);
            const errorMessage = {
                id: Date.now() + 1,
                text: error.response?.data?.error || 'Sorry, I encountered an error. Please try again.',
                sender: 'bot',
                timestamp: new Date().toISOString(),
            };
            setMessages(prev => [...prev, errorMessage]);
            setSuggestions(DEFAULT_SUGGESTIONS);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleChat = () => {
        setIsOpen(!isOpen);
    };

    const handleSuggestionClick = (suggestion) => {
        setInputMessage(suggestion);
        // Auto-send the suggestion message after a brief delay
        setTimeout(() => {
            const form = document.querySelector('form[class*="border-t"]');
            if (form) {
                form.dispatchEvent(new Event('submit', { bubbles: true }));
            }
        }, 100);
    };

    const handleClearChat = async () => {
        if (!sessionId || isClearing) return;

        const userConfirmed = window.confirm('Clear this chat? This will remove all messages for this session.');
        if (!userConfirmed) return;

        setIsClearing(true);
        try {
            await axiosInstance.delete(`/chatbot/delete-history/${sessionId}/`);

            const newSessionId = `web_${Date.now()}_${Math.random().toString(36).substring(7)}`;
            localStorage.setItem('chatbot_session_id', newSessionId);
            setSessionId(newSessionId);
            setMessages([]);
            setSuggestions(DEFAULT_SUGGESTIONS);
        } catch (error) {
            // If 404, session is empty/doesn't exist - that's fine, just create new
            if (error.response?.status === 404) {
                const newSessionId = `web_${Date.now()}_${Math.random().toString(36).substring(7)}`;
                localStorage.setItem('chatbot_session_id', newSessionId);
                setSessionId(newSessionId);
                setMessages([]);
                setSuggestions(DEFAULT_SUGGESTIONS);
                console.log('Chat cleared (session was empty)');
            } else {
                console.error('Error clearing chat history:', error);
                alert('Unable to clear chat right now. Please try again.');
            }
        } finally {
            setIsClearing(false);
        }
    };

    return (
        <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 md:bottom-10 md:right-14 z-50" ref={chatContainerRef}>
            {/* Chat Window */}
            {isOpen && (
                <div className={`mb-4 rounded-lg shadow-2xl w-[90vw] h-[70vh] sm:w-[350px] sm:h-[550px] md:w-[400px] md:h-[600px] max-h-[90vh] max-w-[95vw] flex flex-col overflow-hidden animate-slideUp ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    {/* Header */}
                    <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-3 sm:p-4 flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                            <FaComments className="text-lg sm:text-xl flex-shrink-0" />
                            <div className="min-w-0">
                                <h3 className="font-semibold text-base sm:text-lg truncate">Chat Assistant</h3>
                                <p className="text-xs text-green-100 truncate">We're here to help!</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                                onClick={handleClearChat}
                                disabled={!sessionId || isClearing}
                                className={`p-1.5 sm:p-2 rounded-full transition-colors ${isClearing ? 'opacity-60 cursor-not-allowed' : 'hover:bg-green-800'}`}
                                aria-label="Clear chat"
                                title="Clear chat"
                            >
                                <FaTrash className="text-base sm:text-lg" />
                            </button>
                            <button
                                onClick={toggleChat}
                                className="text-white hover:bg-green-800 p-1.5 sm:p-2 rounded-full transition-colors"
                                aria-label="Close chat"
                            >
                                <FaTimes className="text-lg sm:text-xl" />
                            </button>
                        </div>
                    </div>

                    {/* Messages Area */}
                    <div className={`flex-1 overflow-y-auto p-2 sm:p-4 space-y-2 sm:space-y-3 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
                        {isLoadingHistory ? (
                            <div className={`text-center mt-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                <div className="flex gap-1 justify-center mb-2">
                                    <span className={`w-2 h-2 rounded-full animate-bounce ${isDarkMode ? 'bg-gray-500' : 'bg-gray-400'}`}></span>
                                    <span className={`w-2 h-2 rounded-full animate-bounce delay-100 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-400'}`}></span>
                                    <span className={`w-2 h-2 rounded-full animate-bounce delay-200 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-400'}`}></span>
                                </div>
                                <p className="text-sm">Loading chat history...</p>
                            </div>
                        ) : messages.length === 0 ? (
                            <div className={`text-center mt-6 sm:mt-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                <FaComments className={`text-4xl sm:text-5xl mx-auto mb-2 sm:mb-3 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                                <p className="text-xs sm:text-sm">Start a conversation!</p>
                                <p className="text-xs mt-1 sm:mt-2">Ask me anything about your projects or SEO.</p>
                                
                                
                            </div>
                        ) : (
                            messages.map((msg, index) => (
                                <div key={msg.id}>
                                    <div
                                        className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                                    >
                                        <div className={`max-w-[80%] ${msg.sender === 'user' ? '' : 'w-full max-w-[80%]'}`}>
                                            <div
                                                className={`p-2 sm:p-3 rounded-lg text-sm sm:text-sm ${msg.sender === 'user'
                                                    ? 'bg-[#008300] text-white rounded-br-none'
                                                    : 'bg-green-100 text-gray-800 rounded-bl-none shadow-sm'
                                                    }`}
                                            >
                                                <p className="text-xs sm:text-sm whitespace-pre-wrap break-words">{msg.text}</p>
                                                <p
                                                    className={`text-xs mt-1 ${msg.sender === 'user' 
                                                        ? 'text-green-100' 
                                                        : isDarkMode 
                                                            ? 'text-gray-500' 
                                                            : 'text-gray-400'
                                                    }`}
                                                >
                                                    {formatTimeInKolkata(msg.timestamp)}
                                                </p>
                                            </div>
                                            
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                        {isLoading && (
                            <div className="flex justify-start">
                                <div className={`p-3 rounded-lg rounded-bl-none shadow-sm ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-800'}`}>
                                    <div className="flex gap-1">
                                        <span className={`w-2 h-2 rounded-full animate-bounce ${isDarkMode ? 'bg-gray-500' : 'bg-gray-400'}`}></span>
                                        <span className={`w-2 h-2 rounded-full animate-bounce delay-100 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-400'}`}></span>
                                        <span className={`w-2 h-2 rounded-full animate-bounce delay-200 ${isDarkMode ? 'bg-gray-500' : 'bg-gray-400'}`}></span>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Suggestions Area (always visible) */}
                    {suggestions.length > 0 && (
                        <div className={`px-2 sm:px-4 py-2 border-t ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                            <p className={`text-xs font-semibold mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                Suggestions:
                            </p>
                            <div className="flex flex-col gap-1.5">
                                {suggestions.map((suggestion, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => handleSuggestionClick(suggestion)}
                                        className={`text-left px-2.5 py-1.5 rounded text-xs transition-colors ${
                                            isDarkMode
                                                ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                                        }`}
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Input Area */}
                    <form onSubmit={handleSendMessage} className={`p-2 sm:p-4 border-t ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                        <div className="flex gap-1.5 sm:gap-2">
                            <input
                                ref={inputRef}
                                type="text"
                                value={inputMessage}
                                onChange={(e) => setInputMessage(e.target.value)}
                                placeholder="Type your message..."
                                className={`flex-1 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm border rounded-full focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${isDarkMode ? 'bg-gray-700 text-gray-200 border-gray-600 placeholder-gray-500' : 'bg-white text-gray-800 border-gray-300'}`}
                                disabled={isLoading}
                            />
                            <button
                                type="submit"
                                disabled={isLoading || !inputMessage.trim()}
                                className="bg-[#008300] text-white p-2 sm:p-3 rounded-full hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                                aria-label="Send message"
                            >
                                <FaPaperPlane />
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Chat Toggle Button */}
            <button
                onClick={toggleChat}
                className={`bg-gradient-to-r from-green-600 to-green-700 text-white p-3 sm:p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 ${isOpen ? 'hidden' : 'block'
                    }`}
                aria-label="Open chat"
            >
                <FaComments className="text-2xl" />
                {/* Notification Badge (optional) */}
                {/* <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                    1
                </span> */}
            </button>
        </div>
    );
};

export default Chatbot;